
import React, { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { QueryClientProvider } from "@tanstack/react-query";
import AppRoutes from "./routes";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import OfflineProvider from "./context/offline/OfflineProvider";
import NetworkStatus from "./components/layout/NetworkStatus";
import { ThemeConfigProvider, useThemeConfig } from "./components/ui/theme-config-provider";
import { useSystemBars } from "./hooks/useSystemBars";
import { useCapacitorKeyboard } from "./hooks/useCapacitorKeyboard";
import { useUiPreferences } from "./hooks/useUiPreferences";
import { useRecoveryRedirect } from "./hooks/useRecoveryRedirect";
import { AuthProvider } from "./context/auth/AuthProvider";
import TutorialController from "./components/tutorial/TutorialController";
import BackButtonHandler from "./components/BackButtonHandler";
import PremiumUpgradeModal from "./components/premium/PremiumUpgradeModal";
import { queryClient } from "@/services/queryClient";
import { AppUpdateProvider } from "@/context/app-update/AppUpdateContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import { analyticsInit, trackAppOpen } from "@/lib/analytics";
import { initFacebookSdk } from "@/lib/facebook";
import { initRevenueCat } from "@/services/billing/revenuecat.service";

if (import.meta.env.DEV) {
  (window as any).queryClient = queryClient;
}

const AppInner = () => {
  useThemeConfig();
  useSystemBars();
  useCapacitorKeyboard();
  useRecoveryRedirect();
  const { safeAreaTopOverride, safeAreaBottomOverride } = useUiPreferences();
  const { theme } = useThemeConfig();

  // Apply safe area overrides to CSS variables
  useEffect(() => {
    const root = document.documentElement;
    // Always set the values - null means use default (0px from :root), otherwise use the override
    const topValue = safeAreaTopOverride ?? 0;
    const bottomValue = safeAreaBottomOverride ?? 0;
    root.style.setProperty('--safe-area-top', `${topValue}px`);
    root.style.setProperty('--safe-area-bottom', `${bottomValue}px`);
    console.log('[SafeArea] Updated CSS variables:', { top: topValue, bottom: bottomValue });
  }, [safeAreaTopOverride, safeAreaBottomOverride]);

  return (
    <>
      <Toaster />
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
      <NetworkStatus />
      <PremiumUpgradeModal />
      
      {/* Safe Area Bottom Background Bar */}
      {(safeAreaBottomOverride ?? 0) > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-0 pointer-events-none"
          style={{
            height: `calc(var(--safe-area-bottom, 0px) + env(safe-area-inset-bottom, 0px))`,
            backgroundColor: theme.sidebarColor || '#2f6f31',
          }}
        />
      )}
      
      {/* 
        ========================================================================
        ANDROID BACK BUTTON HANDLER CONFIGURATION
        ========================================================================
        
        This component handles the Android back button behavior with the
        following configuration:
        
        Props:
          - rootPaths: Paths considered as "root" (home/entry points)
          - exitMessage: Toast message shown on first back press at root
          - allowAppExit: Whether to allow app exit on double back press
        
        CRITICAL IMPLEMENTATION REFERENCE:
          1. src/hooks/useBackButtonHandler.ts (725+ lines)
             - Core logic for back button handling
             - Listens for 'backbutton' event from native layer
             - Decides: close keyboard/modal → navigate → exit
             - EXIT MECHANISM: Calls android.exitApp() via JavaScript interface
          
          2. android/app/src/main/java/app/lovable/spesasemplice/MainActivity.java
             - Native handler that intercepts hardware back button
             - Dispatches 'backbutton' event to JavaScript
             - Exposes exitApp() method via @JavascriptInterface for JS to call
             - BYPASS: Works around broken Capacitor App plugin
          
          3. src/components/BackButtonHandler.tsx
             - Simple wrapper component mounting the hook
        
        BEHAVIOR:
          ✓ Close keyboard if open (hideKeyboard)
          ✓ Close modal if open (modalStore.close)
          ✓ On non-root: Navigate back (navigate(-1))
          ✓ On root + allowAppExit=true: Double back to close
          ✓ On root + allowAppExit=false: Show warning toast instead
        
        EXIT FLOW (When Capacitor App plugin unavailable):
          1. User presses back twice on root path
          2. TypeScript checks: typeof window.android.exitApp === 'function'
          3. If true: Calls (window as any).android.exitApp()
          4. Native Java method executes: finishAndRemoveTask()
          5. App closes gracefully
        
        ⚠️ IF THIS BREAKS:
          Check the three files above for native implementation issues
        ======================================================================== 
      */}
      <BackButtonHandler
        rootPaths={["/", "/home"]}
        exitMessage="Premi di nuovo per uscire"
        allowAppExit={false}
      />
    </>
  );
};

const App = () => {
  useEffect(() => {
    analyticsInit().then(trackAppOpen);
    void initFacebookSdk();
    void initRevenueCat();
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeConfigProvider>
        <TooltipProvider>
          <AuthProvider>
            <AppUpdateProvider>
              <TutorialController>
                <OfflineProvider>
                  <BrowserRouter>
                    <HelmetProvider>
                      <AppInner />
                    </HelmetProvider>
                  </BrowserRouter>
                </OfflineProvider>
              </TutorialController>
            </AppUpdateProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeConfigProvider>
    </QueryClientProvider>
  );
};

export default App;
